/**
 * Turns an assistant answer into something worth listening to.
 *
 * Answers are written for the screen: markdown headings, bullet lists, bold
 * figures, occasionally a whole table. Fed straight to TTS that comes out as
 * "star star revenue star star pipe 2.4 million pipe", so everything that is
 * punctuation-for-layout has to go before the text reaches the speech endpoint.
 *
 * This is deliberately a lossy, presentation-level transform - it is only ever
 * used for the audio track. The written answer on screen is untouched.
 */

/**
 * Roughly 45 seconds of speech at the current TTS rate. Past this the listener
 * has stopped following and the screen is the better medium, so we stop at a
 * sentence boundary and say so.
 */
export const MAX_SPOKEN_CHARS = 700;

/** Answer payloads that have no useful spoken form of their own. */
const VISUAL_RESPONSE_TYPES = new Set(["Graph", "Table"]);

export function isVisualResponseType(type: string | undefined): boolean {
  return type !== undefined && VISUAL_RESPONSE_TYPES.has(type);
}

/** Gives a line the terminal punctuation a speech engine needs to pause on. */
function endAsSentence(body: string): string {
  const trimmed = body.trim();
  if (trimmed === "") return "";
  return /[.!?:;,]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * Strips markdown down to prose. Not a parser - a sequence of narrow rules
 * against what the answer assistants actually emit.
 */
export function stripMarkdownForSpeech(markdown: string): string {
  let text = markdown;

  // Fenced code blocks: never speakable.
  text = text.replace(/```[\s\S]*?```/g, " ");
  text = text.replace(/`([^`]+)`/g, "$1");

  // Markdown tables: drop the separator row, then read each remaining row as a
  // comma-separated line so a small table still conveys something.
  text = text
    .split("\n")
    .filter((line) => !/^\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?\s*$/.test(line))
    .map((line) => {
      if (!/^\s*\|.*\|\s*$/.test(line)) return line;
      const cells = line
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean);
      return cells.length > 0 ? cells.join(", ") + "." : "";
    })
    .join("\n");

  // Images before links - an image is not worth announcing at all.
  text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");
  // Links: keep the label, drop the URL.
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");

  // Headings, blockquotes, list bullets: the marker carries no meaning aloud,
  // but the line break does, so end the line as a sentence. Without that the
  // lines are joined by a space and read as one run-on breath, which is most
  // of what makes a spoken list sound robotic.
  text = text.replace(/^\s{0,3}#{1,6}\s+(.*)$/gm, "$1.");
  text = text.replace(/^\s{0,3}>\s?/gm, "");
  text = text.replace(/^\s*[-*+]\s+(.*)$/gm, (_match, body: string) =>
    endAsSentence(body),
  );
  text = text.replace(/^\s*\d+[.)]\s+(.*)$/gm, (_match, body: string) =>
    endAsSentence(body),
  );

  // Horizontal rules.
  text = text.replace(/^\s*([-*_])\s*(?:\1\s*){2,}$/gm, " ");

  // Emphasis markers.
  text = text.replace(/(\*\*|__)(.*?)\1/g, "$2");
  text = text.replace(/(\*|_)(.*?)\1/g, "$2");
  text = text.replace(/~~(.*?)~~/g, "$1");

  // Collapse whitespace. A blank line reads as a sentence break, so it becomes
  // a full stop rather than a space where one is missing.
  text = text.replace(/\n{2,}/g, ". ");
  text = text.replace(/\s*\n\s*/g, " ");
  text = text.replace(/[ \t]{2,}/g, " ");

  // Tidy up punctuation the substitutions above can double up.
  text = text.replace(/\.\s*\.(\s*\.)+/g, ".");
  text = text.replace(/\s+([.,;:!?])/g, "$1");
  text = text.replace(/([.,;:!?])\1+/g, "$1");

  return text.trim();
}

/**
 * Truncates at the last sentence boundary that fits, so speech never stops
 * mid-clause. Falls back to a word boundary when a single sentence is longer
 * than the whole budget.
 */
export function truncateForSpeech(
  text: string,
  maxChars: number = MAX_SPOKEN_CHARS,
): { text: string; truncated: boolean } {
  if (text.length <= maxChars) return { text, truncated: false };

  const window = text.slice(0, maxChars);
  const lastSentenceEnd = Math.max(
    window.lastIndexOf(". "),
    window.lastIndexOf("! "),
    window.lastIndexOf("? "),
  );

  // Only honour a sentence break if it leaves a reasonable amount of speech;
  // otherwise an early first sentence would throw most of the budget away.
  if (lastSentenceEnd > maxChars * 0.4) {
    return {
      text: window.slice(0, lastSentenceEnd + 1).trim(),
      truncated: true,
    };
  }

  const lastSpace = window.lastIndexOf(" ");
  const cut = lastSpace > 0 ? window.slice(0, lastSpace) : window;
  return { text: cut.trim(), truncated: true };
}

export interface SpeakableAnswer {
  /** Text to send to the speech endpoint. Empty when there is nothing to say. */
  text: string;
  /** True when the answer was cut short and the screen holds the rest. */
  truncated: boolean;
}

export interface SpeakableAnswerOptions {
  /** Spoken when the answer is a chart or table with no prose of its own. */
  visualFallback: string;
  /** Appended when the spoken answer was cut short. */
  truncationSuffix: string;
  maxChars?: number;
}

/**
 * Pulls the assistant's own prose explanation out of a chart or table payload.
 *
 * `clarification` is written by the assistant to explain the assumptions,
 * filters and periods behind a result - exactly the part of a visual answer
 * that survives being read aloud. It is optional: the assistant is told to
 * return null when nothing useful can be said.
 */
function visualClarification(response: unknown): string | null {
  if (typeof response !== "object" || response === null) return null;

  const clarification = (response as { clarification?: unknown }).clarification;
  if (typeof clarification !== "string") return null;

  const stripped = stripMarkdownForSpeech(clarification);
  return stripped === "" ? null : stripped;
}

/**
 * Builds the audio track for one answer.
 *
 * Charts and tables carry no prose of their own, so they are spoken as the
 * assistant's clarification (when it wrote one) followed by a pointer to the
 * screen - the clarification explains how the result was arrived at, which is
 * useful aloud, but it is not a substitute for looking at the numbers.
 */
export function toSpeakableAnswer(
  response: unknown,
  responseType: string | undefined,
  options: SpeakableAnswerOptions,
): SpeakableAnswer {
  if (isVisualResponseType(responseType)) {
    const clarification = visualClarification(response);
    if (clarification === null) {
      return { text: options.visualFallback, truncated: false };
    }

    const { text, truncated } = truncateForSpeech(
      clarification,
      options.maxChars,
    );
    return { text: `${text} ${options.visualFallback}`, truncated };
  }

  if (typeof response !== "string") {
    // Any other structured payload: we have no prose, so point at the screen
    // rather than reading JSON aloud.
    return response == null
      ? { text: "", truncated: false }
      : { text: options.visualFallback, truncated: false };
  }

  const stripped = stripMarkdownForSpeech(response);
  if (stripped === "") return { text: "", truncated: false };

  const { text, truncated } = truncateForSpeech(stripped, options.maxChars);
  return {
    text: truncated ? `${text} ${options.truncationSuffix}` : text,
    truncated,
  };
}
